import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/PortfolioShowcase.css';
import { getProjects } from '../api/strapi';

// Portfolio Cover Images (Horizontal Cover Layout Fallbacks)
import HondaCover from '../images/portfolio/covers/honda-cover.jpg';
import FancyFeastCover from '../images/portfolio/covers/fancy-feast-cover.jpg';
import WabtecCover from '../images/portfolio/covers/wabtec-cover.png';
import HDpianoCover from '../images/portfolio/covers/hdpiano-cover.png';
import WalmartCover from '../images/portfolio/covers/walmart-cover.png';
import LittlePassportsCover from '../images/portfolio/covers/little-passports-cover.png';

// Brand Logos (Fallbacks)
import HondaLogo from '../images/portfolio/logos/honda-logo.svg';
import FancyFeastLogo from '../images/portfolio/logos/fancy-feast-logo.png';
import WabtecLogo from '../images/portfolio/logos/wabtec-logo.svg';
import HDpianoLogo from '../images/portfolio/logos/hdpiano-logo.avif';
import WalmartLogo from '../images/portfolio/logos/walmart-logo.svg';
import LittlePassportsLogo from '../images/portfolio/logos/little-passports-logo.png';

const STRAPI_BASE_URL = "http://localhost:1337";

const getFallbackCover = (title, id) => {
  const t = (title || '').toLowerCase();
  if (t.includes('honda')) return HondaCover;
  if (t.includes('fancy feast') || t.includes('nestle') || id === 2) return FancyFeastCover;
  if (t.includes('wabtec') || id === 3) return WabtecCover;
  if (t.includes('hdpiano') || id === 4) return HDpianoCover;
  if (t.includes('walmart') || id === 5) return WalmartCover;
  if (t.includes('little passports') || id === 6) return LittlePassportsCover;
  return HondaCover;
};

const getFallbackLogo = (title, id) => {
  const t = (title || '').toLowerCase();
  if (t.includes('honda')) return HondaLogo;
  if (t.includes('fancy feast') || t.includes('nestle') || id === 2) return FancyFeastLogo;
  if (t.includes('wabtec') || id === 3) return WabtecLogo;
  if (t.includes('hdpiano') || id === 4) return HDpianoLogo;
  if (t.includes('walmart') || id === 5) return WalmartLogo;
  if (t.includes('little passports') || id === 6) return LittlePassportsLogo;
  return null;
};

const getLogoClass = (title, id) => {
  const t = (title || '').toLowerCase();
  if (t.includes('honda')) return 'logo-honda';
  if (t.includes('fancy feast') || t.includes('nestle') || id === 2) return 'logo-fancy-feast';
  if (t.includes('wabtec') || id === 3) return 'logo-wabtec';
  if (t.includes('hdpiano') || id === 4) return 'logo-hdpiano';
  if (t.includes('walmart') || id === 5) return 'logo-walmart';
  if (t.includes('little passports') || id === 6) return 'logo-little-passports';
  return '';
};

const fallbackProjects = [
    {
      "id": 1,
      "title": "Honda",
      "subtitle": "Unified Multi-Brand Sign In Ecosystem",
      "cover_image": HondaCover,
      "card_logo": HondaLogo,
      "overview": "Unified identity management across Honda and Acura platforms.",
      "tags": ["UX Design", "Identity Management", "Enterprise Systems"],
      "url": "/portfolio/1"
    },
    {
      "id": 2,
      "title": "My Fancy Feast",
      "subtitle": "Redesigning the Subscription Box Experience",
      "cover_image": FancyFeastCover,
      "card_logo": FancyFeastLogo,
      "overview": "Streamlined cat profile onboarding and subscription dashboard.",
      "tags": ["UX Design", "Shopify", "E-commerce"],
      "url": "/portfolio/2"
    },
    {
      "id": 3,
      "title": "Wabtec",
      "subtitle": "Next-Generation Digital Railway Operations",
      "cover_image": WabtecCover,
      "card_logo": WabtecLogo,
      "overview": "Optimizing rail freight management dashboard and telemetry.",
      "tags": ["Data Visualization", "Industrial UX", "User Research"],
      "url": "/portfolio/3"
    },
    {
      "id": 4,
      "title": "HDpiano",
      "subtitle": "Elevating the Interactive Piano Learning Experience",
      "cover_image": HDpianoCover,
      "card_logo": HDpianoLogo,
      "overview": "Responsive, interactive piano learning platform built with React.",
      "tags": ["Development", "Responsive", "Interactions"],
      "url": "/portfolio/4"
    },
    {
      "id": 5,
      "title": "Walmart",
      "subtitle": "Innovating the Cat Food Shopping Experience",
      "cover_image": WalmartCover,
      "card_logo": WalmartLogo,
      "overview": "Personalized recommendation engine and dietary filters for Walmart.",
      "tags": ["UX Design", "E-commerce", "Collaboration"],
      "url": "/portfolio/5"
    },
    {
      "id": 6,
      "title": "Little Passports",
      "subtitle": "Interactive Subscription & Discovery Platform",
      "cover_image": LittlePassportsCover,
      "card_logo": LittlePassportsLogo,
      "overview": "Engaging learning subscription experience designed for young explorers.",
      "tags": ["UX Design", "Research", "E-Commerce"],
      "url": "/portfolio/6"
    }
];

const PortfolioShowcase = () => {
    const [projects, setProjects] = useState(fallbackProjects);
    const navigate = useNavigate();

    useEffect(() => {
      getProjects()
        .then((data) => {
          if (data && data.length > 0) {
            const formatted = data.map((item, idx) => {
              const coverUrl = getFallbackCover(item.title, item.id);
              const logoUrl = getFallbackLogo(item.title, item.id);

              const tags = (item.overview_tags && item.overview_tags.length > 0)
                ? item.overview_tags
                : ["UX Design", "Research"];
              const projectUrl = item.locked ? '/portfolio/locked' : `/portfolio/${item.id || idx + 1}`;

              return {
                id: item.id || idx + 1,
                title: item.title,
                subtitle: item.subtitle || item.card_description || '',
                cover_image: coverUrl,
                card_logo: logoUrl,
                overview: item.overview || '',
                tags: tags,
                url: projectUrl
              };
            });
            setProjects(formatted);
          }
        })
        .catch((err) => {
          console.error("Failed to load projects from Strapi into showcase, using fallback:", err);
        });
    }, []);

    return (
      <div className="portfolio-showcase-container hero">
        <div className="container">
          <h1 className="portfolio-title display-3 col-md-6">My Portfolio Projects</h1>
          <p className="portfolio-intro lead col-lg-10 col-md-12 col-sm-12">
            Here, you'll find a showcase of my recent projects, highlighting my expertise in UX design, 
            front-end development, and e-commerce solutions. Each project reflects my commitment to 
            creating user-centered, innovative, and impactful digital experiences.
          </p>
          <div className="row gx-4 gy-4 mt-2">
            {projects.map((project) => (
              <div key={project.id} className="col-lg-4 col-md-6 col-sm-12">
                <div 
                  className="portfolio-card" 
                  onClick={() => navigate(project.url)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="portfolio-image-wrapper">
                    <img
                      src={project.cover_image}
                      alt={project.title}
                      className="portfolio-image"
                    />
                    <div className="portfolio-image-overlay">
                      {project.card_logo && (
                        <img 
                          src={project.card_logo} 
                          alt={`${project.title} logo`} 
                          className={`portfolio-thumbnail-logo ${getLogoClass(project.title, project.id)}`} 
                        />
                      )}
                    </div>
                  </div>
                  <div className="portfolio-content">
                    <h5>{project.title}</h5>
                    <h6 className="portfolio-subtitle">{project.subtitle}</h6>
                    <div className="portfolio-tags-container">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="portfolio-tag">{tag}</span>
                      ))}
                    </div>
                    <Link to={project.url} className="portfolio-link">View Case Study &rarr;</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  export default PortfolioShowcase;


